export const LEGACY_PERSISTENCE_VERSION = -1;
export const CURRENT_PERSISTENCE_VERSION = 1;

export type PersistedEnvelope = {
  persistFilters?: string;
  wialonStore?: string;
  _persist?: string;
};

export type PersistedStateResult = {
  snapshot: PersistedEnvelope | null;
  serialized: string | null;
  repaired: boolean;
};

export type StorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<unknown>;
  removeItem(key: string): Promise<unknown>;
};

type RecordValue = Record<string, unknown>;

const isRecord = (value: unknown): value is RecordValue =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const isValidPersistFilters = (value: unknown): value is RecordValue =>
  isRecord(value);

const isValidWialonStore = (value: unknown): value is RecordValue => {
  if (!isRecord(value)) return false;

  const accessToken = value.accessToken;
  return (
    accessToken === undefined ||
    accessToken === null ||
    typeof accessToken === 'string'
  );
};

const isValidPersistMetadata = (value: unknown): value is RecordValue => {
  if (!isRecord(value) || !Number.isInteger(value.version)) return false;

  return value.rehydrated === undefined || typeof value.rehydrated === 'boolean';
};

const emptyResult = (repaired: boolean): PersistedStateResult => ({
  snapshot: null,
  serialized: null,
  repaired,
});

export function sanitizePersistedState(
  raw: string | null
): PersistedStateResult {
  if (raw === null) {
    return emptyResult(false);
  }

  const decoded = parseJson(raw);
  if (!isRecord(decoded)) {
    return emptyResult(true);
  }

  let repaired = Object.keys(decoded).some(
    key => !['persistFilters', 'wialonStore', '_persist'].includes(key)
  );
  const snapshot: PersistedEnvelope = {};

  const persistFilters = decoded.persistFilters;
  if (persistFilters !== undefined) {
    if (typeof persistFilters !== 'string') {
      repaired = true;
    } else {
      const decodedFilters = parseJson(persistFilters);
      if (isValidPersistFilters(decodedFilters)) {
        snapshot.persistFilters = persistFilters;
      } else {
        repaired = true;
      }
    }
  }

  const wialonStore = decoded.wialonStore;
  if (wialonStore !== undefined) {
    if (typeof wialonStore !== 'string') {
      repaired = true;
    } else {
      const decodedWialonStore = parseJson(wialonStore);
      if (isValidWialonStore(decodedWialonStore)) {
        snapshot.wialonStore = wialonStore;
      } else {
        repaired = true;
      }
    }
  }

  const persistMetadata = decoded._persist;
  if (persistMetadata !== undefined) {
    if (typeof persistMetadata !== 'string') {
      repaired = true;
    } else {
      const decodedMetadata = parseJson(persistMetadata);
      if (isValidPersistMetadata(decodedMetadata)) {
        const version = decodedMetadata.version as number;
        if (
          version !== LEGACY_PERSISTENCE_VERSION &&
          version !== CURRENT_PERSISTENCE_VERSION
        ) {
          return emptyResult(true);
        }
        snapshot._persist = persistMetadata;
      } else {
        repaired = true;
      }
    }
  }

  return {
    snapshot,
    serialized: JSON.stringify(snapshot),
    repaired,
  };
}

export function createSafePersistStorage(
  storage: StorageLike,
  rootKey: string
): StorageLike {
  return {
    getItem: async key => {
      let raw: string | null;

      try {
        raw = await storage.getItem(key);
      } catch {
        return null;
      }

      if (key !== rootKey || raw === null) {
        return raw;
      }

      const result = sanitizePersistedState(raw);
      if (result.serialized === null) {
        try {
          await storage.removeItem(key);
        } catch {
          // Recovery in memory is still sufficient for the current bootstrap.
        }
        return null;
      }

      if (result.repaired) {
        try {
          await storage.setItem(key, result.serialized);
        } catch {
          // The sanitized value is still returned to the current rehydration.
        }
      }

      return result.serialized;
    },
    setItem: (key, value) => storage.setItem(key, value),
    removeItem: key => storage.removeItem(key),
  };
}

export async function migratePersistedState(
  state: { _persist?: { version?: number } } | undefined,
  currentVersion: number
) {
  if (!state) return undefined;

  const inboundVersion =
    state._persist?.version ?? LEGACY_PERSISTENCE_VERSION;

  if (
    inboundVersion === LEGACY_PERSISTENCE_VERSION ||
    inboundVersion === currentVersion
  ) {
    return state;
  }

  return undefined;
}
